import { CountryCodeType } from '../common';

export enum LanguageOption {
  ENGLISH = 'en',
  SPANISH = 'es',
  JAPANESE = 'ja',
  PORTUGUESE = 'pt',
  HINDI = 'hi',
}

/**
 * Supported language codes for URL routing
 * These are the 2-letter codes used in URLs (e.g., /en, /es, /ja)
 */
export const SUPPORTED_LANGUAGE_CODES = ['en', 'es', 'ja', 'pt', 'hi'] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGE_CODES)[number];

/**
 * Maps LanguageOption enum values to URL language codes
 */
export const LANGUAGE_OPTION_TO_URL_CODE: Record<LanguageOption, string> = {
  [LanguageOption.ENGLISH]: 'en',
  [LanguageOption.SPANISH]: 'es',
  [LanguageOption.JAPANESE]: 'ja',
  [LanguageOption.PORTUGUESE]: 'pt',
  [LanguageOption.HINDI]: 'hi',
};

/**
 * Maps URL language codes to LanguageOption enum values
 */
export const URL_CODE_TO_LANGUAGE_OPTION: Record<string, LanguageOption> = {
  en: LanguageOption.ENGLISH,
  es: LanguageOption.SPANISH,
  ja: LanguageOption.JAPANESE,
  pt: LanguageOption.PORTUGUESE,
  hi: LanguageOption.HINDI,
};

/**
 * Converts LanguageOption enum value to URL code
 * @param languageOption - The LanguageOption enum value
 * @returns The corresponding URL code (e.g., 'en', 'es', 'ja')
 */
export const getUrlCodeFromLanguageOption = (
  languageOption: LanguageOption,
): string => {
  return LANGUAGE_OPTION_TO_URL_CODE[languageOption] || 'en';
};

/**
 * Converts URL code to LanguageOption enum value
 * @param urlCode - The URL language code (e.g., 'en', 'es', 'ja')
 * @returns The corresponding LanguageOption enum value
 */
export const getLanguageOptionFromUrlCode = (
  urlCode: string,
): LanguageOption => {
  return URL_CODE_TO_LANGUAGE_OPTION[urlCode] || LanguageOption.ENGLISH;
};

/**
 * Checks if a string is a valid supported language code
 * @param code - The code to check
 * @returns True if the code is a supported language code
 */
export const isSupportedLanguageCode = (
  code: string,
): code is SupportedLanguageCode => {
  return SUPPORTED_LANGUAGE_CODES.includes(code as SupportedLanguageCode);
};

export type Languages = `${LanguageOption}`;

export const LANGUAGE_DETAILS: Record<
  LanguageOption,
  { label: string; countryCode: CountryCodeType; desc: string }
> = {
  [LanguageOption.ENGLISH]: {
    label: 'English',
    countryCode: 'GB',
    desc: 'English',
  },
  [LanguageOption.SPANISH]: {
    label: 'Español',
    countryCode: 'ES',
    desc: 'Spanish',
  },
  [LanguageOption.JAPANESE]: {
    label: '日本語',
    countryCode: 'JP',
    desc: 'Japanese',
  },
  [LanguageOption.PORTUGUESE]: {
    label: 'Português',
    countryCode: 'PT',
    desc: 'Portuguese',
  },
  [LanguageOption.HINDI]: {
    label: 'हिन्दी',
    countryCode: 'IN',
    desc: 'Hindi',
  },
};

export const getLanguageDetails = (language: string) => {
  if (language in LANGUAGE_DETAILS) {
    return LANGUAGE_DETAILS[language as LanguageOption];
  }
  return LANGUAGE_DETAILS[LanguageOption.ENGLISH];
};

export const LANGUAGE_FLAG_COLORS: Record<Languages, string> = {
  en: '#1E3A8A',
  es: '#C60B1E',
  pt: '#006600',
  ja: '#000000',
  hi: '#FF9933',
};
